/**
 * Pipeline Utility Functions
 *
 * Pipeline JSON generation from the step-list model.
 * Preview DSL composition for chained step previews.
 */

import yaml from 'js-yaml';
import {
  fullOutputTable,
  type PipelineTrigger,
  type TransformStep,
  type PipelineChart,
  type AlertStep,
  type IngestStep,
  type AgentStep,
} from '@/types/pipeline';
import type { DataSource } from '@solidafy/api-client';
import type { SeedTableDef } from '@/types/seed';

// ============================================================================
// Preview DSL Composition — chained dependency preview
// ============================================================================

/**
 * Split a DSL block into statements (separated by blank lines) and assign
 * `varName` only to the terminal expression — the statement that does NOT
 * start with `identifier = `.
 *
 * Example input (varName = "repo_velocity"):
 *   prs = pr_metrics.group_by([repo]).agg([...])
 *
 *   commit_activity.group_by([repo]).join(prs, ...)
 *
 * Output:
 *   prs = pr_metrics.group_by([repo]).agg([...])
 *
 *   repo_velocity = commit_activity.group_by([repo]).join(prs, ...)
 */
function assignToTerminal(varName: string, dsl: string): string {
  // Split into statements on blank-line boundaries.
  // A "statement" is a group of consecutive non-blank lines.
  const statements = splitStatements(dsl);

  if (statements.length === 0) {
    return `${varName} = ${dsl}`;
  }

  // Single statement — check for bare variable ref on last line (visual builder output)
  if (statements.length === 1) {
    const lines = statements[0].split('\n');
    const lastNonEmpty =
      lines
        .filter((l) => l.trim())
        .pop()
        ?.trim() || '';
    if (/^\w+$/.test(lastNonEmpty)) {
      // Bare variable ref — replace it with assignment
      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim() === lastNonEmpty) {
          lines[i] = `${varName} = ${lastNonEmpty}`;
          break;
        }
      }
      return lines.join('\n');
    }
    // Single chained expression — prepend assignment
    return `${varName} = ${statements[0]}`;
  }

  // Multiple statements — find the terminal expression (no `identifier = ` prefix).
  // The assignment pattern: line starts with `word = ` (but NOT `==`).
  const assignmentRe = /^\w+\s*=[^=]/;

  let terminalIdx = -1;
  for (let i = statements.length - 1; i >= 0; i--) {
    const firstLine = statements[i].trimStart();
    if (!assignmentRe.test(firstLine)) {
      terminalIdx = i;
      break;
    }
  }

  if (terminalIdx === -1) {
    // All statements are assignments — the last one is the result.
    // Shouldn't normally happen, but handle gracefully: wrap last in reassignment.
    terminalIdx = statements.length - 1;
    // Strip existing assignment from last statement
    const last = statements[terminalIdx];
    const eqIdx = last.indexOf('=');
    if (eqIdx !== -1) {
      const rhs = last.slice(eqIdx + 1).trimStart();
      statements[terminalIdx] = `${varName} = ${rhs}`;
    }
  } else {
    statements[terminalIdx] = `${varName} = ${statements[terminalIdx]}`;
  }

  return statements.join('\n\n');
}

/**
 * Split DSL text into statements separated by blank lines.
 * Preserves original whitespace within each statement.
 */
function splitStatements(dsl: string): string[] {
  const lines = dsl.split('\n');
  const statements: string[] = [];
  let current: string[] = [];

  for (const line of lines) {
    if (line.trim() === '') {
      if (current.length > 0) {
        statements.push(current.join('\n'));
        current = [];
      }
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) {
    statements.push(current.join('\n'));
  }
  return statements;
}

/**
 * Compose a single DSL script that chains all dependent steps together
 * so the target step can be previewed even if its dependencies haven't
 * been materialized yet.
 *
 * For each dependency step (in topological order):
 *   1. Include its DSL code
 *   2. Assign the result to a variable named after its outputTable
 *
 * For the target step:
 *   1. Replace any source("...outputTable") references with the variable name
 *   2. Append its DSL code
 */
/**
 * Strip a trailing .output(...) terminal from DSL, handling nested parentheses
 * (e.g. partition_by: [col.truncate(day)]). Scans from the end of the string
 * and counts parens to find the matching close of .output(.
 */
function stripOutputTerminal(dsl: string): string {
  const marker = '.output(';
  const idx = dsl.lastIndexOf(marker);
  if (idx === -1) {
    return dsl.trimEnd();
  }

  // Walk forward from the opening '(' counting depth
  let depth = 0;
  for (let i = idx + marker.length - 1; i < dsl.length; i++) {
    if (dsl[i] === '(') {
      depth++;
    } else if (dsl[i] === ')') {
      depth--;
      if (depth === 0) {
        // Everything from idx to i (inclusive) is the .output() call
        return dsl.slice(0, idx).trimEnd();
      }
    }
  }
  // Unbalanced — fall back to slicing at the marker
  return dsl.slice(0, idx).trimEnd();
}

export function composePreviewDsl(targetStep: TransformStep, allSteps: TransformStep[], namespace?: string): string {
  const stepByName = new Map(allSteps.map((s) => [s.name, s]));
  const resolved: TransformStep[] = []; // ordered list of resolved deps
  const resolvedNames = new Set<string>();
  const dslBlocks: string[] = [];

  // Recursively resolve dependencies (topological order)
  function resolve(step: TransformStep) {
    if (resolvedNames.has(step.name)) {
      return;
    }
    for (const depName of step.dependsOn) {
      const dep = stepByName.get(depName);
      if (dep) {
        resolve(dep);
      }
    }
    resolvedNames.add(step.name);
    if (step.id !== targetStep.id) {
      resolved.push(step);
    }
  }

  // Resolve all dependencies of the target step
  for (const depName of targetStep.dependsOn) {
    const dep = stepByName.get(depName);
    if (dep) {
      resolve(dep);
    }
  }

  // Helper: replace source() refs to any already-processed step with its variable name.
  // When source("table") is followed by a newline + indented .method(), join them
  // onto one line so the DSL parser sees a connected chain (bare variables on their
  // own line are treated as complete expressions by the parser).
  function replaceSourceRefs(dsl: string, processedSoFar: TransformStep[]): string {
    let result = dsl;
    for (const dep of processedSoFar) {
      const table = fullOutputTable(dep);
      if (!table) {
        continue;
      }
      const escaped = table.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Pass 1: source("table")\n  .method → variable.method  (keep chain connected)
      result = result.replace(new RegExp(`source\\(\\s*"${escaped}"\\s*\\)\\s*\\n\\s*\\.`, 'g'), `${dep.name}.`);
      // Pass 2: remaining source("table") without continuation (e.g. in assignment RHS)
      result = result.replace(new RegExp(`source\\(\\s*"${escaped}"\\s*\\)`, 'g'), dep.name);
    }
    return result;
  }

  // For each dependency (topological order): replace source() refs to earlier
  // steps, then assign result to step NAME as variable
  const processed: TransformStep[] = [];
  for (const step of resolved) {
    if (!step.dsl?.trim() || !step.name) {
      continue;
    }

    const varName = step.name;
    // Replace source() refs to steps already processed before this one
    const dsl = replaceSourceRefs(step.dsl.trim(), processed);

    // Split into statements (separated by blank lines) and find the terminal
    // expression — the one that does NOT start with `identifier = `.
    // Prepend `varName = ` only to that terminal statement; leave intermediate
    // assignments (e.g. `prs = ...`) untouched.
    dslBlocks.push(assignToTerminal(varName, dsl));

    processed.push(step);
  }

  // Process the target step's DSL — replace source() refs to ALL resolved deps.
  // Strip any .output() terminal — it's a write operation and will cause a parse
  // error if sent to the relay as a query during preview.
  // Uses paren-counting from the end to handle nested parens like partition_by: [col.truncate(day)].
  const targetDsl = stripOutputTerminal(replaceSourceRefs(targetStep.dsl?.trim() || '', processed));

  let composed = dslBlocks.length > 0 ? dslBlocks.join('\n\n') + '\n\n' + targetDsl : targetDsl;

  // Substitute @{NAMESPACE} and ${NAMESPACE} template variables
  if (namespace) {
    composed = composed.replace(/@\{NAMESPACE\}/g, namespace);
    composed = composed.replace(/\$\{NAMESPACE\}/g, namespace);
  }

  return composed;
}

/**
 * Compose a DSL script for chart preview by creating a virtual TransformStep
 * from the chart definition and delegating to composePreviewDsl().
 */
export function composeChartPreviewDsl(chart: PipelineChart, allSteps: TransformStep[], namespace?: string): string {
  const virtual: TransformStep = {
    id: `chart_${chart.id}`,
    name: chart.name,
    dsl: chart.dsl,
    dependsOn: chart.dependsOn,
    outputNamespace: '',
    outputTable: '',
    mode: 'replace',
    conflictKeys: [],
    transformNodes: [],
    transformEdges: [],
    order: -1,
  };
  return composePreviewDsl(virtual, allSteps, namespace);
}

/**
 * Compose a DSL script for alert preview by creating a virtual TransformStep
 * from the alert definition and delegating to composePreviewDsl().
 */
export function composeAlertPreviewDsl(alert: AlertStep, allSteps: TransformStep[], namespace?: string): string {
  const virtual: TransformStep = {
    id: `alert_${alert.id}`,
    name: alert.name,
    dsl: alert.dsl,
    dependsOn: alert.dependsOn,
    outputNamespace: '',
    outputTable: '',
    mode: 'replace',
    conflictKeys: [],
    transformNodes: [],
    transformEdges: [],
    order: -1,
  };
  return composePreviewDsl(virtual, allSteps, namespace);
}

// ============================================================================
// Execute Step — append .output() terminal for materialization
// ============================================================================

/**
 * Append a `.output(...)` terminal to composed DSL for step execution.
 *
 * Produces:
 *   .output("namespace.table", mode: replace)
 *   .output("namespace.table", mode: upsert, keys: [col1, col2])
 *
 * Returns null if step has no outputTable configured.
 */
export function appendOutputTerminal(dsl: string, step: TransformStep): string | null {
  const table = fullOutputTable(step);
  if (!table) {
    return null;
  }

  const mode = step.mode || 'replace';
  let terminal = `.output("${table}", mode: ${mode})`;

  if ((mode === 'merge' || mode === 'upsert') && step.conflictKeys?.length) {
    const keys = step.conflictKeys.map((k) => k.trim()).join(', ');
    terminal = `.output("${table}", mode: ${mode}, keys: [${keys}])`;
  }

  return dsl.trimEnd() + '\n' + terminal;
}

// ============================================================================
// Pipeline JSON Generation (v2 — step-list model)
// ============================================================================

export function generatePipelineJson(
  trigger: PipelineTrigger,
  transformSteps: TransformStep[],
  dataSources: DataSource[],
  charts?: PipelineChart[],
  alerts?: AlertStep[],
  seedTables?: SeedTableDef[],
  ingestSteps?: IngestStep[],
  agentSteps?: AgentStep[],
): Record<string, unknown> | null {
  const pipeline: Record<string, unknown> = { version: 2 };
  const steps: Record<string, unknown>[] = [];

  // ── Connector steps (from ingestSteps) ──
  if (ingestSteps) {
    for (const step of ingestSteps) {
      if (!step.name) {
        continue;
      }
      const connEntry: Record<string, unknown> = {
        name: step.name,
        type: 'connector',
        dsl: step.dsl,
        depends_on: step.dependsOn.length > 0 ? step.dependsOn : undefined,
        data_source_id: step.dataSourceId || undefined,
      };
      if (step.dependsOnFailure?.length) {
        connEntry.depends_on_failure = step.dependsOnFailure;
      }
      steps.push(connEntry);
    }
  }

  // ── Materialize steps (from transformSteps) ──
  for (const step of transformSteps) {
    const mat: Record<string, unknown> = {
      name: step.name,
      type: 'materialize',
      dsl: step.dsl,
      output: fullOutputTable(step),
      mode: step.mode || 'replace',
    };

    if ((step.mode === 'merge' || step.mode === 'upsert') && step.conflictKeys?.length) {
      mat.keys = step.conflictKeys;
    }
    if (step.dependsOn?.length) {
      mat.depends_on = step.dependsOn;
    }
    if (step.partitionBy?.length) {
      mat.partition_by = step.partitionBy;
    }
    if (step.dependsOnFailure?.length) {
      mat.depends_on_failure = step.dependsOnFailure;
    }
    steps.push(mat);
  }

  // ── Seed steps (from seedTables) ──
  if (seedTables) {
    for (const s of seedTables) {
      const seedEntry: Record<string, unknown> = {
        name: s.name,
        type: 'seed',
        output: s.output,
        mode: s.mode || 'merge',
        primary_key: s.primary_key,
        columns: s.columns,
        dsl: s.dsl || undefined,
        default_rows: s.default_rows || undefined,
        depends_on: s.depends_on || undefined,
        description: s.description || undefined,
      };
      if (s.depends_on_failure?.length) {
        seedEntry.depends_on_failure = s.depends_on_failure;
      }
      steps.push(seedEntry);
    }
  }

  // ── Alert steps (from alerts) ──
  if (alerts) {
    for (const a of alerts) {
      const alertEntry: Record<string, unknown> = {
        name: a.name,
        type: 'alert',
        title: a.title,
        description: a.description || undefined,
        severity: a.severity,
        dsl: a.dsl,
        channels: a.channels,
        depends_on: a.dependsOn.length > 0 ? a.dependsOn : undefined,
        trigger: a.trigger || undefined,
      };
      if (a.dependsOnFailure?.length) {
        alertEntry.depends_on_failure = a.dependsOnFailure;
      }
      steps.push(alertEntry);
    }
  }

  // ── Agent steps (from agentSteps) ──
  if (agentSteps) {
    for (const ag of agentSteps) {
      if (!ag.name) {
        continue;
      }
      const agentEntry: Record<string, unknown> = {
        name: ag.name,
        type: 'agent',
        repo: ag.repo,
        task: ag.task,
        phase: ag.phase === 'execute' ? 'execute' : 'plan',
        base_branch: ag.baseBranch || undefined,
        data_source: ag.dataSource || undefined,
        ticket: ag.ticket || undefined,
        depends_on: ag.dependsOn.length > 0 ? ag.dependsOn : undefined,
      };
      if (ag.dependsOnFailure?.length) {
        agentEntry.depends_on_failure = ag.dependsOnFailure;
      }
      steps.push(agentEntry);
    }
  }

  pipeline.steps = steps;

  // ── Output — solidafy catalog (key + namespace from data source) ──
  // Prefer namespace from first linked ingest step DS, fall back to trigger DS for old pipelines
  const linkedIngest = ingestSteps?.find((s) => s.dataSourceId);
  const dsForNamespace = linkedIngest
    ? dataSources.find((d) => d.id === linkedIngest.dataSourceId)
    : trigger.dataSourceId
      ? dataSources.find((d) => d.id === trigger.dataSourceId)
      : null;
  pipeline.output = {
    format: 'solidafy',
    solidafy: {
      api_key: '${SOLIDAFY_API_KEY}',
      namespace: dsForNamespace?.namespace || 'default',
    },
  };

  // ── Charts (top-level) ──
  if (charts && charts.length > 0) {
    pipeline.charts = charts.map((c) => ({
      name: c.name,
      subtitle: c.subtitle || undefined,
      description: c.description || undefined,
      dsl: c.dsl,
      tab: c.tab || undefined,
      depends_on: c.dependsOn,
      grid: c.grid || undefined,
    }));
  }

  return pipeline;
}

// ============================================================================
// YAML Generation — builder state → YAML string
// ============================================================================

type YamlGeneratorInput = {
  ingestSteps: IngestStep[];
  transformSteps: TransformStep[];
  charts: PipelineChart[];
  alerts: AlertStep[];
  seedTables: SeedTableDef[];
};

/**
 * Generate a v2 pipeline YAML string from the current builder state.
 * Produces clean YAML matching the registry template format.
 */
export function generatePipelineYaml(input: YamlGeneratorInput): string {
  const { ingestSteps, transformSteps, charts, alerts, seedTables } = input;
  const doc: Record<string, unknown> = { version: 2 };
  const steps: Record<string, unknown>[] = [];

  // Connector steps
  for (const step of ingestSteps) {
    if (!step.name) {
      continue;
    }
    const s: Record<string, unknown> = {
      name: step.name,
      type: 'connector',
      dsl: step.dsl || undefined,
    };
    if (step.dependsOn.length > 0) {
      s.depends_on = step.dependsOn;
    }
    if (step.dataSourceId) {
      s.data_source_id = step.dataSourceId;
    }
    steps.push(s);
  }

  // Materialize steps
  for (const step of transformSteps) {
    const s: Record<string, unknown> = {
      name: step.name,
      type: 'materialize',
      dsl: step.dsl || undefined,
      output: fullOutputTable(step) || undefined,
      mode: step.mode || 'replace',
    };
    if ((step.mode === 'merge' || step.mode === 'upsert') && step.conflictKeys?.length) {
      s.keys = step.conflictKeys;
    }
    if (step.dependsOn?.length) {
      s.depends_on = step.dependsOn;
    }
    if (step.partitionBy?.length) {
      s.partition_by = step.partitionBy;
    }
    steps.push(s);
  }

  // Seed steps
  for (const s of seedTables) {
    const step: Record<string, unknown> = {
      name: s.name,
      type: 'seed',
      output: s.output,
      mode: s.mode || 'merge',
    };
    if (s.primary_key?.length) {
      step.primary_key = s.primary_key;
    }
    if (s.columns?.length) {
      step.columns = s.columns.map((c) => {
        const col: Record<string, unknown> = { name: c.name };
        if (c.type) {
          col.type = c.type;
        }
        return col;
      });
    }
    if (s.dsl) {
      step.dsl = s.dsl;
    }
    if (s.default_rows?.length) {
      step.default_rows = s.default_rows;
    }
    if (s.depends_on?.length) {
      step.depends_on = s.depends_on;
    }
    if (s.description) {
      step.description = s.description;
    }
    steps.push(step);
  }

  // Alert steps
  for (const a of alerts) {
    const s: Record<string, unknown> = {
      name: a.name,
      type: 'alert',
      title: a.title || undefined,
      severity: a.severity,
      dsl: a.dsl || undefined,
    };
    if (a.description) {
      s.description = a.description;
    }
    if (a.channels?.length) {
      s.channels = a.channels;
    }
    if (a.dependsOn?.length) {
      s.depends_on = a.dependsOn;
    }
    if (a.trigger) {
      s.trigger = a.trigger;
    }
    steps.push(s);
  }

  doc.steps = steps;

  // Charts
  if (charts.length > 0) {
    doc.charts = charts.map((c) => {
      const chart: Record<string, unknown> = {
        name: c.name,
        dsl: c.dsl,
      };
      if (c.subtitle) {
        chart.subtitle = c.subtitle;
      }
      if (c.description) {
        chart.description = c.description;
      }
      if (c.dependsOn?.length) {
        chart.depends_on = c.dependsOn;
      }
      if (c.tab) {
        chart.tab = c.tab;
      }
      if (c.grid) {
        chart.grid = c.grid;
      }
      return chart;
    });
  }

  return yaml.dump(doc, {
    lineWidth: 120,
    noRefs: true,
    quotingType: '"',
    forceQuotes: false,
  });
}

// ============================================================================
// YAML Parsing — YAML string → partial builder state
// ============================================================================

type ParsedPipelineState = {
  ingestSteps: IngestStep[];
  transformSteps: TransformStep[];
  charts: PipelineChart[];
  alerts: AlertStep[];
  seedTables: SeedTableDef[];
};

function generateStepId(): string {
  return `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse a v2 pipeline YAML string back into builder state objects.
 * Throws on invalid YAML.
 */
export function parsePipelineYaml(yamlStr: string): ParsedPipelineState {
  const doc = yaml.load(yamlStr) as Record<string, unknown>;
  if (!doc || typeof doc !== 'object') {
    throw new Error('Invalid YAML: expected an object');
  }

  const rawSteps = Array.isArray(doc.steps) ? (doc.steps as Record<string, unknown>[]) : [];
  const ingestSteps: IngestStep[] = [];
  const transformSteps: TransformStep[] = [];
  const alerts: AlertStep[] = [];
  const seedTables: SeedTableDef[] = [];

  for (const [idx, step] of rawSteps.entries()) {
    const name = (step.name as string) || '';
    const dsl = (step.dsl as string) || '';
    const dependsOn = Array.isArray(step.depends_on) ? (step.depends_on as string[]) : [];
    const stepType = (step.type as string) || '';

    if (stepType === 'connector') {
      const connectorMatch = dsl.match(/connector\("([^"]+)"/);
      const streamMatch = dsl.match(/\.stream\("([^"]+)"/);
      ingestSteps.push({
        id: generateStepId(),
        name,
        dsl,
        connectorType: connectorMatch?.[1] || '',
        streamName: streamMatch?.[1] || '',
        dataSourceId: (step.data_source_id as string) || undefined,
        order: idx,
        dependsOn,
      });
    } else if (stepType === 'materialize') {
      const output = (step.output as string) || '';
      const dotIdx = output.indexOf('.');
      transformSteps.push({
        id: generateStepId(),
        name,
        dsl,
        outputNamespace: dotIdx > 0 ? output.slice(0, dotIdx) : '',
        outputTable: dotIdx > 0 ? output.slice(dotIdx + 1) : output,
        mode: (step.mode as TransformStep['mode']) || 'replace',
        conflictKeys: Array.isArray(step.keys) ? (step.keys as string[]) : [],
        partitionBy: Array.isArray(step.partition_by) ? (step.partition_by as string[]) : [],
        dependsOn,
        transformNodes: [],
        transformEdges: [],
        order: idx,
      });
    } else if (stepType === 'seed') {
      seedTables.push({
        name,
        output: (step.output as string) || '',
        mode: (step.mode as 'merge' | 'replace') || 'merge',
        primary_key: Array.isArray(step.primary_key) ? (step.primary_key as string[]) : [],
        columns: Array.isArray(step.columns)
          ? (step.columns as Record<string, unknown>[]).map((c) => ({
              name: (c.name as string) || '',
              type: c.type as string | undefined,
            }))
          : [],
        dsl: dsl || undefined,
        default_rows: Array.isArray(step.default_rows) ? (step.default_rows as Record<string, unknown>[]) : undefined,
        depends_on: dependsOn.length > 0 ? dependsOn : undefined,
        description: (step.description as string) || undefined,
      });
    } else if (stepType === 'alert') {
      const rawTrigger = step.trigger as string | undefined;
      const trigger =
        rawTrigger === 'on_success' || rawTrigger === 'on_failure' || rawTrigger === 'on_complete' ? rawTrigger : null;
      alerts.push({
        id: generateStepId(),
        name,
        title: (step.title as string) || name,
        description: (step.description as string) || '',
        severity: (step.severity as AlertStep['severity']) || 'warning',
        dsl,
        channels: Array.isArray(step.channels) ? (step.channels as string[]) : [],
        dependsOn,
        order: idx,
        trigger,
      });
    }
  }

  // Parse charts
  const rawCharts = Array.isArray(doc.charts) ? (doc.charts as Record<string, unknown>[]) : [];
  const charts: PipelineChart[] = rawCharts.map((c) => ({
    id: generateStepId(),
    name: (c.name as string) || '',
    subtitle: (c.subtitle as string) || undefined,
    description: (c.description as string) || undefined,
    dsl: (c.dsl as string) || '',
    dependsOn: Array.isArray(c.depends_on) ? (c.depends_on as string[]) : [],
    tab: (c.tab as string) || undefined,
    grid: c.grid as PipelineChart['grid'],
  }));

  return { ingestSteps, transformSteps, charts, alerts, seedTables };
}
